import RequireSubmittingUser from "HoC/RequireSubmittingUser";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectToken } from "redux-store/features/authSlice";
import CountryService from "services/CountryService";
import { useForm } from "shared/helpers";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faMapPin } from "@fortawesome/free-solid-svg-icons";
import LocationCenterService from "services/LocationCenterService";
import NewLocationMessage from "./modals/NewLocationMessage";

export default function AddVendorLocation({ closeModal }) {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);

  const { form, onChange } = useForm();

  const countryService = new CountryService(token, dispatch);
  const locationCenterService = new LocationCenterService(token, dispatch);

  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [towns, setTowns] = useState([]);
  const [showNotListed, setShowNotListed] = useState(false);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await countryService.getNames();
        setCountries(res.data?.data);

        if (res.data?.meta.default_country) {
          onChange("country", res.data.meta.default_country);
        }
      } catch (e) {
        console.error("Error fetching countries:", e);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const res = await locationCenterService.getCountryRegions(form.country);
        setRegions(res.data);
      } catch (e) {}
    };

    setRegions([]);

    if (form.country) {
      fetchRegions();
    }
  }, [form.country]);

  useEffect(() => {
    const fetchTowns = async () => {
      try {
        const res = await locationCenterService.getCountryRegionTowns(
          form.country,
          form.region,
        );
        setTowns(res.data);
      } catch (e) {}
    };

    setTowns([]);

    if (form.country && form.region) {
      fetchTowns();
    }
  }, [form.country, form.region]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const location = towns.find(
        (t) => t.location_id === Number(form.location_id),
      );
      closeModal(location);
    },
    [form.location_id, towns],
  );

  const handlePostcodeSubmit = useCallback(() => {}, []);

  const openNotListed = useCallback(() => setShowNotListed(true), []);
  const closeNotListed = useCallback(() => setShowNotListed(false), []);

  /** Case filed - there is no location to hand back, so just close the form. */
  const handleNotListedSent = useCallback(() => {
    setShowNotListed(false);
    closeModal();
  }, [closeModal]);

  return (
    <RequireSubmittingUser>
      <div id="main-container">
        <div className="container">
          <h1 className="page-title mb-4">Add New Vendor Location</h1>

          <form onSubmit={handleSubmit}>
            {/** Country */}
            <div className="mb-4">
              <label className="form-label">
                Country <em>*</em>
              </label>
              <select
                className="form-select"
                value={form.country}
                onChange={(e) => onChange("country", e.target.value)}
              >
                {countries.map((country) => {
                  return (
                    <option key={country.country_id} value={country.country_id}>
                      {country.name}
                    </option>
                  );
                })}
              </select>
            </div>

            {/** Search with postcode */}
            <div className="row g-4 mb-4">
              {/** Input */}
              <div className="col-md-8 col-lg-10">
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0">
                    <FontAwesomeIcon icon={faMapPin} className="text-muted" />
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Enter postcode or location"
                    aria-label="Postcode search"
                  />
                </div>
              </div>

              {/** Search button */}
              <div className="col-md-4 col-lg-2">
                <button
                  className="btn btn-yellow w-100 h-100"
                  type="button"
                  onClick={() => handlePostcodeSubmit()}
                >
                  <FontAwesomeIcon icon={faSearch} className="me-2" />
                  Search
                </button>
              </div>
            </div>

            {/** Region & Town */}
            <div className="row g-4 mb-4">
              <div className="col-md-6">
                <label className="form-label">
                  Region <em>*</em>
                </label>

                <select
                  className="form-select"
                  value={form.region || ""}
                  onChange={(e) => onChange("region", e.target.value)}
                >
                  <option value="" selected>
                    Select region
                  </option>
                  {regions.map((item) => {
                    return (
                      <option key={item.region} value={item.region}>
                        {item.region}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  Town <em>*</em>
                </label>

                <select
                  className="form-select"
                  value={form.location_id || ""}
                  onChange={(e) => onChange("location_id", e.target.value)}
                >
                  <option value="" selected>
                    Select town
                  </option>
                  {towns.map((item) => {
                    return (
                      <option key={item.location_id} value={item.location_id}>
                        {item.town}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div className="mb-5"></div>

            {/** Action row centered */}
            <div className="d-flex justify-content-center gap-3">
              <button
                className="btn btn-black"
                type="button"
                onClick={openNotListed}
              >
                Not listed
              </button>

              <button className="btn btn-yellow" type="submit">
                Submit
              </button>
            </div>
          </form>

          {/** Missing location request */}
          <NewLocationMessage
            show={showNotListed}
            handleClose={closeNotListed}
            onSent={handleNotListedSent}
          />
        </div>
      </div>
    </RequireSubmittingUser>
  );
}
